import React, { useMemo } from "react";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";
import {
  AREAS,
  SEMANAS,
  META_MENSUAL,
  obtenerNombreMes,
  totalRegistro,
  leerRegistros,
} from "./datosFinancieros";

const ORDEN_LEYENDA = ["Meta", "TotalAcumulado", ...AREAS.map((a) => a.nombre)];
const ordenarLeyenda = (item) => ORDEN_LEYENDA.indexOf(item.dataKey);

const Dashboard = () => {
  const nombreMes = useMemo(obtenerNombreMes, []);
  const registros = leerRegistros();

  const datos = AREAS.map(({ nombre }) => {
    const encontrado = registros.find((d) => d.area === nombre && d.mes === nombreMes);
    return (
      encontrado ||
      SEMANAS.reduce((acc, semana) => ({ ...acc, [semana]: 0 }), { area: nombre })
    );
  });

  const totalesSemana = SEMANAS.map((semana) =>
    datos.reduce((acc, item) => acc + Number(item[semana] || 0), 0)
  );

  const totalAcumulado = totalesSemana.reduce((acc, valor) => acc + valor, 0);
  const cumplioMeta = totalAcumulado >= META_MENSUAL;
  const estadoMeta = cumplioMeta ? "Meta Cumplida" : "Meta No Cumplida";

  const datosGrafica = SEMANAS.map((semana, index) => ({
    semana: `Semana ${index + 1}`,
    ...datos.reduce(
      (acc, item) => ({ ...acc, [item.area]: Number(item[semana] || 0) }),
      {}
    ),
    Meta: META_MENSUAL,
    TotalAcumulado: totalesSemana.slice(0, index + 1).reduce((acc, v) => acc + v, 0),
  }));

  const descargarCSV = () => {
    const encabezados = "Area,Semana1,Semana2,Semana3,Semana4,Total\n";

    const filas = datos
      .map(
        (item) =>
          `${item.area},${SEMANAS.map((s) => item[s]).join(",")},${totalRegistro(item)}`
      )
      .join("\n");

    const resumen =
      "\n\nResumen Mensual\n" +
      `Meta Mensual,${META_MENSUAL}\n` +
      `Acumulado,${totalAcumulado}\n` +
      `Estado,${estadoMeta}\n`;

    const blob = new Blob([encabezados + filas + resumen], {
      type: "text/csv;charset=utf-8;",
    });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    const fecha = new Date();
    const mes = fecha.toLocaleDateString("es-MX", { month: "long" }).toLowerCase();

    link.href = url;
    link.download = `reporte-${mes}-${fecha.getFullYear()}.csv`;
    link.click();

    URL.revokeObjectURL(url);
  };

  return (
    <div className="page-card">
      <div className="dashboard-header">
        <div>
          <h1 className="page-title">Dashboard Financiero</h1>
          <p className="dashboard-subtitle">Resumen de {nombreMes}</p>
        </div>

        <button type="button" onClick={descargarCSV} className="dashboard-button">
          Descargar CSV
        </button>
      </div>

      <div className="dashboard-metrics">
        <div className="fc-stat">
          <div className="fc-stat-label">Meta Mensual</div>
          <div className="fc-stat-value">{META_MENSUAL}</div>
        </div>
        <div className="fc-stat">
          <div className="fc-stat-label">Total Acumulado</div>
          <div className="fc-stat-value">{totalAcumulado}</div>
        </div>
        <div
          className="fc-stat"
          style={{ background: cumplioMeta ? "#dcfce7" : "#fee2e2" }}
        >
          <div className="fc-stat-label">Estado</div>
          <div
            className="fc-stat-value"
            style={{ color: cumplioMeta ? "#166534" : "#991b1b", fontSize: "28px" }}
          >
            {estadoMeta}
          </div>
        </div>
      </div>

      <div className="fc-table-container">
        <table className="dashboard-table dashboard-table--striped">
          <thead>
            <tr className="dashboard-table-header">
              {["Área", "Semana 1", "Semana 2", "Semana 3", "Semana 4", "Total"].map(
                (titulo) => (
                  <th key={titulo}>{titulo}</th>
                )
              )}
            </tr>
          </thead>

          <tbody>
            {datos.map((item) => (
              <tr key={item.area}>
                <td className="fc-bold">{item.area}</td>
                {SEMANAS.map((semana) => (
                  <td key={semana}>{item[semana]}</td>
                ))}
                <td className="fc-highlight">{totalRegistro(item)}</td>
              </tr>
            ))}

            <tr className="dashboard-total-row">
              <td>TOTAL GENERAL</td>
              {totalesSemana.map((valor, index) => (
                <td key={SEMANAS[index]}>{valor}</td>
              ))}
              <td className="fc-highlight">{totalAcumulado}</td>
            </tr>
          </tbody>
        </table>
      </div>

      <div className="chart-container">
        <h2 className="chart-title">Evolución Semanal por Área</h2>

        <ResponsiveContainer width="100%" height={400}>
          <LineChart data={datosGrafica}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="semana" />
            <YAxis />
            <Tooltip itemSorter={ordenarLeyenda} />
            <Legend itemSorter={ordenarLeyenda} />

            <Line
              type="monotone"
              dataKey="Meta"
              stroke="#f59e0b"
              strokeWidth={3}
              strokeDasharray="8 4"
            />
            <Line
              type="monotone"
              dataKey="TotalAcumulado"
              name="Total Acumulado"
              stroke="#000000"
              strokeWidth={4}
            />
            {AREAS.map((area) => (
              <Line
                key={area.id}
                type="monotone"
                dataKey={area.nombre}
                stroke={area.color}
                strokeWidth={3}
              />
            ))}
          </LineChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
};

export default Dashboard;
